import { useCallback, useState } from 'react';
import type { ListingDetails } from 'app/types/listingDetails.type';
import { emitTrackRequestContactStartEvent } from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import FullscreenOverlay from '../../../ui/FullscreenOverlay';
import { useDetailPanelSafe } from '../../../ui/DetailPanel';
import { ContactFormSpoke } from './ContactFormSpoke';
import { ContactFormSpokeContent } from './ContactFormSpokeContent';

/**
 * Hook that provides a handler to open the contact form from anywhere.
 *
 * Desktop (with DetailPanel): opens ContactFormSpokeContent in the DetailPanel.
 * Mobile / no DetailPanel: manages a FullscreenOverlay — caller must render `contactFormOverlay` in their JSX.
 *
 * Safe to use outside of a DetailPanelProvider (e.g., on the SRP's FeedView).
 *
 * When a listing is provided, it's forwarded to ContactFormSpoke so the form
 * doesn't depend on Redux's currentListingDetails (which may not be populated
 * outside the HDP).
 */
export const useContactFormAction = (listing?: ListingDetails) => {
  const detailPanel = useDetailPanelSafe();
  const [isOpen, setIsOpen] = useState(false);

  const useOverlay = !detailPanel || detailPanel.isMobile;

  const openContactForm = useCallback(() => {
    emitTrackRequestContactStartEvent({
      listing,
      triggerLocation: 'home_details',
      triggerObject: 'home_details_component|modal_contact_form',
    });

    if (detailPanel && !detailPanel.isMobile) {
      detailPanel.openPanel('contact-form', ContactFormSpokeContent, { config: { listing } });
    } else {
      setIsOpen(true);
    }
  }, [detailPanel, listing]);

  const contactFormOverlay = useOverlay ? (
    <FullscreenOverlay.Root open={isOpen} onOpenChange={setIsOpen}>
      <FullscreenOverlay.Portal>
        <FullscreenOverlay.Backdrop>
          <FullscreenOverlay.Content>
            <FullscreenOverlay.Header title="Contact property" />
            <FullscreenOverlay.Body padding="edge-to-edge">
              <ContactFormSpoke onClose={() => setIsOpen(false)} listing={listing} />
            </FullscreenOverlay.Body>
          </FullscreenOverlay.Content>
        </FullscreenOverlay.Backdrop>
      </FullscreenOverlay.Portal>
    </FullscreenOverlay.Root>
  ) : null;

  return { openContactForm, contactFormOverlay };
};
